import React, { useEffect, useMemo, useRef, useState } from "react";
import { useGetReservationAndServiceCountofAllWorkshopsQuery } from "../../../app/store/api/workshopApi";
import WorkshopCard from "./WorkshopCard";
import { Box, Button, Card, InputAdornment, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import CardSkeleton from "../../../shared/components/CardSkeleton";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useWindowSize } from "../../../shared/hooks/useWindowSize";
import AddWorkshop from "./AddWorkshop/AddWorkshop";

const DEBOUNCE_DELAY = 500;

const WorkshopsList = ({ workshopsList, isLoading }) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const debounceRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const [reservationServiceCount, setReservationServiceCount] = useState({});
  const {data:reservation_service_count, isLoading: isLoadingReservationServiceCount} = useGetReservationAndServiceCountofAllWorkshopsQuery();
  const [isAddWorkshop, setIsAddWorkshop] = useState(false);

  useEffect(() => {
    if (reservation_service_count) {
      setReservationServiceCount(reservation_service_count);
    }
  }, [reservation_service_count]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    setIsSearching(true);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(value.trim().toLowerCase());
    }, DEBOUNCE_DELAY);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const filteredWorkshopsList = useMemo(() => {
    if (!debouncedSearchTerm) return workshopsList;

    return workshopsList?.filter((workshop) => {
      const name = workshop?.name?.toLowerCase() || "";
      const address = workshop?.address?.toLowerCase() || "";
      return name.includes(debouncedSearchTerm) || address.includes(debouncedSearchTerm);
    });
  }, [workshopsList, debouncedSearchTerm]);

  useEffect(() => {
    setIsSearching(false);
  }, [filteredWorkshopsList]);

  const handleAddWorkshopClick = () => {
    setIsAddWorkshop(true);
  };

  const handleCloseAddWorkshop = () => {
    window.scrollTo(0, 0);
    setIsAddWorkshop(false);
  };

  const showSkeleton = isLoading || isSearching || isLoadingReservationServiceCount;

  if(isAddWorkshop){
    return <AddWorkshop onClose={handleCloseAddWorkshop} />
  }

  return (
    <>
      <Box>
        <Box sx={{ display: "flex", flexDirection: isMobile ? "column" :"row", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 2 }}>
          <TextField
            fullWidth
            name='search'
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder={t("adminDashboard.searchPlaceHolder") || "Search by name or city"}
            variant='outlined'
            size='small'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button variant='contained' size='medium' fullWidth={isMobile} onClick={handleAddWorkshopClick} startIcon={<AddIcon fontSize='small' />} sx={{ bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" }, whiteSpace: "nowrap" }}>
            {t("adminDashboard.addWorkshop")}
          </Button>
        </Box>
      </Box>
      <Box>
        {showSkeleton ? (
          <CardSkeleton />
        ) : filteredWorkshopsList?.length === 0 ? (
          <Card elevation={0} sx={{ p: 4, textAlign: "center", border: "1px dashed #E3E7EE" }}>
            <Typography color='text.secondary'>{t("adminDashboard.noWorkshop")}</Typography>
          </Card>
        ) : (
          filteredWorkshopsList?.map((workshop, index) => (
            <Box key={workshop.id} mb={1}>
              <WorkshopCard workshop={workshop} index={index} reservationServiceCount={reservationServiceCount[workshop.id]}/>
            </Box>
          ))
        )}
      </Box>
    </>
  );
};

export default WorkshopsList;
